import { CommonModule } from '@angular/common';
import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatTableModule } from '@angular/material/table';
import { Subject, takeUntil } from 'rxjs';
import { LoadingBoxComponent } from '../../../../../shared/components/loading-box/loading-box.component';
import { AbntPipe } from '../../../../../shared/pipes/abnt.pipe';
import { AlertService, AlertType } from '../../../../../shared/services/alert.service';
import { ProcessoVendaService } from '../../../../../shared/services/apis/vendas/processo-venda.service';
import { VendaTipoDocumentoEnum } from '../../../../../shared/enums/vendas/venda-tipo-documento.enum';
import { VendaFiltroRequest } from '../../../../../shared/services/requests/vendas/venda-filtro.request';
import { PvLinhaListaVm, PvRaiaVm, formatarNumeroDocumento, montarLinhasLista } from '../../processo-venda.models';

export interface PvImpressaoListaDialogData {
    /** Os MESMOS filtros que a Lista está usando — imprimir é levar ao papel o que está na tela. */
    filtro: VendaFiltroRequest;

    /**
     * As raias já montadas, de onde saem nome e cor da etapa de cada linha.
     *
     * Vêm de fora porque essa tradução é do board: o endpoint de impressão devolve documentos com
     * idEtapaKanban, e refazer a resolução aqui seria uma segunda cópia de `montarLinhasLista`.
     */
    raias: PvRaiaVm[];

    /** Texto do período aplicado, para o cabeçalho do papel dizer de que recorte ele é. */
    rotuloPeriodo: string;
}

/**
 * Impressão da visão em Lista.
 *
 * Respeita os filtros e NÃO pagina: o que vai ao papel é a coleção inteira que o filtro encontra,
 * não a página que está na tela. Segue o padrão das outras listagens do HUB (produto, pessoa,
 * contas a pagar) — tabela dentro de `.modal-print-container` e `window.open` copiando os estilos.
 */
@Component({
    selector: 'app-pv-impressao-lista-dialog',
    standalone: true,
    imports: [
        CommonModule,
        MatDialogModule,
        MatButtonModule,
        MatIconModule,
        MatTableModule,
        LoadingBoxComponent,
        AbntPipe,
    ],
    templateUrl: './pv-impressao-lista-dialog.component.html',
    styleUrls: ['./pv-impressao-lista-dialog.component.css'],
})
export class PvImpressaoListaDialogComponent implements OnInit, OnDestroy {
    public isCarregando = true;
    public linhas: PvLinhaListaVm[] = [];
    public totalRegistros = 0;

    /** A coleção passou do teto do servidor e veio cortada. */
    public isTruncado = false;
    public tetoImpressao = 0;

    /** Sem Tags e sem Integração: no papel elas viram ruído e roubam largura das colunas que contam. */
    public readonly colunas: string[] = ['numero', 'cliente', 'processo', 'etapa', 'responsavel', 'data', 'valor'];

    public readonly impressoEm = new Date();

    private readonly destroy$ = new Subject<void>();

    constructor(
        public dialogRef: MatDialogRef<PvImpressaoListaDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: PvImpressaoListaDialogData,
        private processoVendaService: ProcessoVendaService,
        private alertService: AlertService,
    ) {}

    public ngOnInit(): void {
        this.carregar();
    }

    public ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    private carregar(): void {
        this.processoVendaService
            .getListaPrint(this.data.filtro)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: resposta => {
                    this.isCarregando = false;

                    if (resposta.isError) {
                        this.alertService.show('Atenção', resposta.errorDescription, AlertType.warning);
                        this.fechar();
                        return;
                    }

                    this.linhas = montarLinhasLista(resposta.itens ?? [], this.data.raias);
                    this.totalRegistros = resposta.totalRegistros;
                    this.isTruncado = resposta.isTruncado;
                    this.tetoImpressao = resposta.tetoImpressao;
                },
                error: () => {
                    this.isCarregando = false;
                    this.alertService.show('Erro', 'Não foi possível montar a listagem para impressão.', AlertType.error);
                    this.fechar();
                },
            });
    }

    public numeroExibido(linha: PvLinhaListaVm): string {
        return formatarNumeroDocumento(linha.documento.numero, linha.documento.idVendaTipoDocumento);
    }

    /** Modelo é molde: no papel, como no board e na Lista, a coluna Data sai vazia. */
    public ehModelo(linha: PvLinhaListaVm): boolean {
        return linha.documento.idVendaTipoDocumento === VendaTipoDocumentoEnum.Modelo;
    }

    public get somaValorTotal(): number {
        return this.linhas.reduce((soma, linha) => soma + (linha.documento.valorTotal ?? 0), 0);
    }

    /**
     * Abre uma janela só com o conteúdo do relatório e manda imprimir.
     *
     * Copia as folhas de estilo da página porque a janela nova não herda nada — é o mesmo caminho
     * das outras impressões do HUB. O try/catch em volta das cssRules é necessário: folha de outra
     * origem lança ao ser lida, e uma fonte externa derrubaria a impressão inteira.
     */
    public imprimir(): void {
        const conteudo = document.querySelector('.modal-print-container');
        if (!conteudo) return;

        const janela = window.open('', '', 'height=600,width=800');
        if (!janela) return;

        const estilos = Array.from(document.styleSheets)
            .map(folha => {
                try {
                    return Array.from(folha.cssRules)
                        .map(regra => regra.cssText)
                        .join('');
                } catch {
                    return '';
                }
            })
            .join('');

        janela.document.write(`
            <html>
                <head>
                    <title>Processo de Venda</title>
                    <base href="/">
                    <style>
                        ${estilos}

                        /* A tela é uma viewport travada com rolagem própria; o papel é o oposto. */
                        html, body {
                            height: auto !important;
                            min-height: 0 !important;
                            overflow: visible !important;
                            margin: 20px;
                            font-family: Roboto, "Helvetica Neue", sans-serif;
                            color: #000;
                            background: #fff;
                        }

                        .no-print { display: none !important; }

                        .modal-print-container {
                            display: block !important;
                            width: 100% !important;
                            height: auto !important;
                            min-height: 0 !important;
                            overflow: visible !important;
                            box-shadow: none !important;
                            margin: 0 !important;
                            padding: 0 !important;
                        }

                        /* O dark mode não vai para o papel: branco no branco não imprime. */
                        .mat-mdc-table { background: #fff !important; }
                        .mat-mdc-header-cell {
                            color: #000 !important;
                            font-weight: bold !important;
                            border-bottom: 2px solid #000 !important;
                        }
                        .mat-mdc-cell { color: #000 !important; border-bottom: 1px solid #ddd !important; }

                        /* Cabeçalho da tabela repetido em toda página de um relatório longo. */
                        thead { display: table-header-group; }
                        tr { break-inside: avoid; }
                    </style>
                </head>
                <body>${conteudo.innerHTML}</body>
            </html>
        `);

        janela.document.close();
        janela.focus();

        // O print precisa da janela já com layout pronto; sem a folga ele sai em branco.
        setTimeout(() => {
            janela.print();
            janela.close();
        }, 800);
    }

    public fechar(): void {
        this.dialogRef.close();
    }
}
